import { syncServerClock } from "./clock";

/** Error returned by the API as `{ error: { code, message, param? } }`, or a network failure. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly param: string | undefined;

  constructor(status: number, code: string, message: string, param?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.param = param;
  }
}

function isErrorBody(v: unknown): v is { error: { code: string; message: string; param?: string } } {
  if (typeof v !== "object" || v === null || !("error" in v)) return false;
  const e = (v as { error: unknown }).error;
  return typeof e === "object" && e !== null && "code" in e && "message" in e;
}

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  body?: unknown;
  /** Sent as `Authorization: Bearer …` (e.g. a checkout token). */
  bearer?: string;
  /** Called on 401 before the error is thrown (e.g. to send the user to sign-in). */
  onUnauthorized?: () => void;
}

/** JSON request to the UnizPay API; non-2xx responses throw `ApiError`. */
export async function requestJson<T>(method: HttpMethod, url: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.bearer) headers.Authorization = `Bearer ${opts.bearer}`;
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "same-origin",
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch {
    throw new ApiError(0, "network_error", "Could not reach UnizPay. Check your connection and try again.");
  }

  syncServerClock(res.headers.get("Date"));

  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!res.ok) {
    if (res.status === 401) opts.onUnauthorized?.();
    if (isErrorBody(data))
      throw new ApiError(res.status, data.error.code, data.error.message, data.error.param);
    throw new ApiError(res.status, "http_error", `Request failed (HTTP ${res.status})`);
  }
  return data as T;
}

export function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}
